import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError, photoUrl } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { Lock, Send, BadgeCheck } from "lucide-react";

export default function Chat() {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [needsSub, setNeedsSub] = useState(false);
  const [params] = useSearchParams();
  const pollerRef = useRef(null);
  const endRef = useRef(null);

  const loadConvos = async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      setConvos(data);
      const openId = params.get("open");
      const matchId = params.get("match");
      let target = null;
      if (openId) target = data.find((c) => c.id === openId);
      else if (matchId) target = data.find((c) => c.match_id === matchId);
      if (!active && (target || data.length > 0)) setActive(target || data[0]);
    } catch (e) {
      if (e?.response?.status === 402) { setNeedsSub(true); return; }
      setErr(formatApiError(e));
    }
  };

  useEffect(() => { loadConvos(); /* eslint-disable-next-line */ }, [params.toString()]);

  useEffect(() => {
    if (!active) return;
    let since = null;
    const fetchMsgs = async () => {
      try {
        const { data } = await api.get(`/chat/conversations/${active.id}/messages${since ? `?since=${encodeURIComponent(since)}` : ""}`);
        if (data.length > 0) {
          if (!since) setMessages(data);
          else setMessages((m) => [...m, ...data]);
          since = data[data.length - 1].created_at;
          setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        } else if (!since) {
          setMessages([]);
        }
      } catch (e) {
        if (e?.response?.status === 402) setNeedsSub(true);
      }
    };
    fetchMsgs();
    pollerRef.current = setInterval(fetchMsgs, 4000);
    return () => clearInterval(pollerRef.current);
  }, [active?.id]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post(`/chat/conversations/${active.id}/messages`, { text });
      setMessages((m) => [...m, data]);
      setText("");
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (needsSub) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="chat-needs-sub">
        <Lock className="h-6 w-6 mx-auto text-gold-500" />
        <h1 className="mt-5 font-serif text-3xl text-olive-700">Samtaler er en medlemsfunktion</h1>
        <p className="mt-3 text-brown-600">Private, filtrerede samtaler er kun tilgængelige for medlemmer.</p>
        <Link to="/pricing" className="btn-primary mt-8 inline-flex" data-testid="chat-cta-pricing">Se planer</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="chat-page">
      <div className="eyebrow">Private samtaler</div>
      <h1 className="mt-1 font-serif text-4xl text-olive-700">Samtaler</h1>
      {err && <div className="mt-4 text-destructive text-sm">{err}</div>}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 min-h-[60vh]">
        <aside className="card-soft overflow-hidden">
          <div className="p-4 border-b border-sand-300 text-sm eyebrow">Matches</div>
          {convos.length === 0 && <div className="p-6 text-sm text-brown-500">Ingen samtaler endnu. Accepter en henvendelse for at begynde.</div>}
          <ul className="divide-y divide-sand-200">
            {convos.map((c) => {
              const op = c.other_profile;
              return (
                <li key={c.id}>
                  <button
                    onClick={() => setActive(c)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition ${active?.id === c.id ? "bg-olive-100" : "hover:bg-sand-100"}`}
                    data-testid={`convo-${c.id}`}
                  >
                    <div className="h-11 w-11 rounded-full bg-gradient-to-br from-sand-200 to-olive-100 overflow-hidden shrink-0 border-2 border-white shadow-sm ring-1 ring-sand-300">
                      {op?.avatar ? (
                        <img
                          src={photoUrl(op.avatar)}
                          alt={op?.display_name || ""}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-olive-700 font-serif text-lg">{(op?.display_name || "?")[0]}</div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-olive-700 truncate">{op?.display_name || "—"}</div>
                      <div className="text-xs text-brown-500 truncate">{c.last_message_preview || "Sig salaam…"}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="card-soft flex flex-col min-h-[60vh]">
          {active ? (
            <>
              <div className="border-b border-sand-300 p-4 flex items-center gap-3">
                <Link
                  to={active.other_profile?.id ? `/matches/${active.other_profile.id}` : "#"}
                  className="h-14 w-14 rounded-full bg-gradient-to-br from-sand-200 to-olive-100 overflow-hidden shrink-0 border-2 border-white shadow-md ring-1 ring-sand-300 hover:scale-105 transition"
                  data-testid="chat-header-avatar">
                  {active.other_profile?.avatar ? (
                    <img
                      src={photoUrl(active.other_profile.avatar)}
                      alt={active.other_profile.display_name || ""}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-olive-700 font-serif text-xl">{(active.other_profile?.display_name || "?")[0]}</div>
                  )}
                </Link>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-xl text-olive-700 truncate flex items-center gap-1.5">
                    {active.other_profile?.display_name}
                    {active.other_profile?.verified && <BadgeCheck className="h-4 w-4 text-sky-600 fill-sky-100" />}
                  </div>
                  <div className="text-xs text-brown-500 truncate">
                    {active.other_profile?.city}
                    {active.other_profile?.city && active.other_profile?.profession ? " • " : ""}
                    {active.other_profile?.profession}
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-sand-50/50">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-olive-500 text-white" : "bg-white border border-sand-300 text-brown-600"}`}>
                        {m.text}
                        {m.filtered && <div className={`mt-1 text-[10px] ${mine ? "text-white/80" : "text-brown-500"} italic`}>filtreret for upassende sprog</div>}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-sand-300 p-3 flex gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Skriv en respektfuld besked…"
                  className="field flex-1"
                  data-testid="chat-input"
                />
                <button type="submit" className="btn-primary px-5 py-2" data-testid="chat-send">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-brown-500 text-sm p-6">Vælg en samtale for at begynde.</div>
          )}
        </section>
      </div>
    </div>
  );
}
